/**
 * Typed nlocal API client.
 *
 * Thin wrapper over `openapi-fetch` bound to the generated `paths` type, so every call site
 * is checked against the control plane's OpenAPI contract (§5). Adds the three things every
 * nlocal request needs and the spec does not express: Sanctum's same-site cookie credentials,
 * the CSRF token those cookies do not by themselves prove, and the `X-Workspace-Id` scoping
 * header (§5, §6.1, §12.3).
 */
import createClient, { type Middleware } from 'openapi-fetch'
import type { paths } from './schema'

export type { paths, components, operations } from './schema'

/** §5 error envelope. Every non-2xx response from the API has this shape. */
export interface ApiError {
  error: {
    code: string
    message: string
    docs?: string
    details?: Record<string, unknown>
  }
}

export interface ClientOptions {
  /** e.g. `https://api.nlocal.dev/v1` */
  baseUrl: string
  /** Resolves the active workspace id for the `X-Workspace-Id` header. */
  workspaceId?: () => string | null | undefined
  /** Bearer token for CLI/API-key callers. Omit in the browser — Sanctum uses cookies. */
  token?: () => string | null | undefined
}

/** The methods Laravel exempts from CSRF, so the ones the token is not needed on. */
const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

/**
 * Sanctum's CSRF token, read back out of the cookie Laravel set.
 *
 * `credentials` below sends that cookie, and on its own it proves nothing: a browser attaches
 * cookies to forged cross-site requests too, which is the whole point of CSRF. So Laravel
 * compares the session's token against one the *caller* had to read and echo back — `_token`
 * in a form body, or this header. Axios does it unprompted, which is why Laravel's SPA
 * documentation never mentions the step; `openapi-fetch` is plain `fetch`, and does not.
 *
 * Read per request rather than once at import. `session()->regenerate()` runs on every login
 * and register (§5.1) and rotates the token, so a value captured at start-up is stale from the
 * first successful sign-in onwards — and the replacement arrives on that very response, which
 * a fresh read picks up without a second handshake.
 */
function xsrfToken(): string | null {
  // A Node caller — CLI, SSR, a test harness — has no cookie jar, and authenticates with the
  // bearer token above instead.
  if (typeof document === 'undefined') return null

  const value = /(?:^|;\s*)XSRF-TOKEN=([^;]*)/.exec(document.cookie)?.[1]
  if (value === undefined) return null

  // Decoded, because the cookie is URL-encoded and the payload is base64: its `=` padding
  // arrives as `%3D`, and the raw value fails validation.
  return decodeURIComponent(value)
}

export function createApiClient({ baseUrl, workspaceId, token }: ClientOptions) {
  const client = createClient<paths>({
    baseUrl,
    // Sanctum SPA auth is cookie based (§6.1). 'same-origin' rather than 'include' because
    // Laravel serves the dashboard from the API's own origin (DECISIONS.md D-009); callers
    // that do run cross-origin pass their own fetch options.
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })

  const scope: Middleware = {
    async onRequest({ request }) {
      const ws = workspaceId?.()
      if (ws) request.headers.set('X-Workspace-Id', ws)

      const bearer = token?.()
      if (bearer) request.headers.set('Authorization', `Bearer ${bearer}`)

      // Only where Laravel checks it. Withholding it on reads also keeps the token out of
      // requests a cross-origin caller may make with its own fetch options.
      if (!READ_METHODS.has(request.method)) {
        const xsrf = xsrfToken()
        if (xsrf !== null) request.headers.set('X-XSRF-TOKEN', xsrf)
      }

      return request
    },
  }

  client.use(scope)
  return client
}

export type ApiClient = ReturnType<typeof createApiClient>

/** Narrow an unknown rejection to the §5 error envelope. */
export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as ApiError).error?.code === 'string'
  )
}
